import { useState, useEffect, useCallback } from "react";
import { Search, Shield, UserCog, Eye, Loader2, ChevronLeft, ChevronRight, Globe, Building2, FolderCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuth() {
  const token = localStorage.getItem("admin_token") || localStorage.getItem("app_token");
  return token ? { authorization: `Bearer ${token}` } : {};
}

const ROLE_CONFIG = {
  user: { label: "MyGoals User", color: "#64748B", icon: Eye },
  admin: { label: "MyGoals Admin", color: "#194449", icon: UserCog },
  microsoft_manager: { label: "Microsoft Manager", color: "#2563EB", icon: Building2 },
  resource_content_manager: { label: "Content Manager", color: "#07641D", icon: FolderCog },
  super_admin: { label: "Super Admin", color: "#DC2626", icon: Shield },
};

export default function AppUserManagement() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [updatingUser, setUpdatingUser] = useState(null);
  const pageSize = 25;

  // Solution areas (loaded once for RCM role assignment)
  const [solutionAreas, setSolutionAreas] = useState([]);

  // Role-edit dialog state
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [editRole, setEditRole] = useState("user");
  const [editSolutionArea, setEditSolutionArea] = useState("all");
  const [saving, setSaving] = useState(false);

  // Fetch solution areas on mount
  useEffect(() => {
    axios.get(`${API}/auth/solution-areas`, { headers: getAuth() })
      .then(res => setSolutionAreas(res.data.solution_areas || []))
      .catch(() => {});
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (roleFilter !== "all") params.append("role", roleFilter);
      if (search.trim()) params.append("search", search.trim());
      const res = await axios.get(`${API}/auth/app-users?${params}`, { headers: getAuth() });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("You don't have permission to manage LevelUp users");
      } else {
        toast.error("Failed to load users");
      }
    } finally {
      setLoading(false);
    }
  }, [page, roleFilter, search]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openRoleEditor = (user) => {
    setEditDialog({ open: true, user });
    setEditRole(user.role || "user");
    setEditSolutionArea(user.assigned_solution_area || "all");
  };

  const saveRoleChange = async () => {
    if (!editDialog.user) return;
    setSaving(true);
    try {
      const payload = { role: editRole };
      if (editRole === "resource_content_manager") {
        payload.assigned_solution_area = editSolutionArea === "all" ? null : editSolutionArea;
      } else {
        // Clear solution area when switching away from RCM
        payload.assigned_solution_area = null;
      }
      await axios.put(`${API}/auth/app-users/${editDialog.user.user_id}/role`, payload, { headers: getAuth() });
      const saLabel = editSolutionArea === "all" ? "All Solution Areas" : solutionAreas.find(s => s.key === editSolutionArea)?.label || editSolutionArea;
      toast.success(
        editRole === "resource_content_manager"
          ? `Role updated to Content Manager (${saLabel})`
          : "Role updated successfully"
      );
      setEditDialog({ open: false, user: null });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    setUpdatingUser(userId);
    try {
      await axios.put(`${API}/auth/app-users/${userId}/status?status=${newStatus}`, {}, { headers: getAuth() });
      toast.success(`User ${newStatus === "active" ? "activated" : "deactivated"}`);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingUser(null);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-[#091F2C]" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="app-users-heading">
          LevelUp User Management
        </h2>
        <p className="text-sm text-[#64748B] mt-1">
          Manage roles for LevelUp SSO authenticated users. Changes take effect on next login.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name or email..."
            className="w-full border border-black/10 rounded-full pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#194449]/20"
            data-testid="app-users-search"
          />
        </div>
        <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]" data-testid="app-users-role-filter">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="user">MyGoals User</SelectItem>
            <SelectItem value="admin">MyGoals Admin</SelectItem>
            <SelectItem value="microsoft_manager">Microsoft Manager</SelectItem>
            <SelectItem value="resource_content_manager">Content Manager</SelectItem>
            <SelectItem value="super_admin">Super Admin</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-[#94A3B8]" data-testid="app-users-total">{total} users</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-black/5 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#194449]" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-sm text-[#64748B]">
            {search ? `No users matching the search criteria.` : "No LevelUp users have signed in yet."}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F4F3F5]">
                  <TableHead className="text-xs font-medium text-[#64748B]">User</TableHead>
                  <TableHead className="text-xs font-medium text-[#64748B]">Domain Type</TableHead>
                  <TableHead className="text-xs font-medium text-[#64748B]">Role</TableHead>
                  <TableHead className="text-xs font-medium text-[#64748B]">Solution Area</TableHead>
                  <TableHead className="text-xs font-medium text-[#64748B]">Status</TableHead>
                  <TableHead className="text-xs font-medium text-[#64748B]">Last Login</TableHead>
                  <TableHead className="text-xs font-medium text-[#64748B] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => {
                  const rc = ROLE_CONFIG[u.role] || ROLE_CONFIG.user;
                  const RoleIcon = rc.icon;
                  const saDisplay = u.assigned_solution_area
                    ? (solutionAreas.find(s => s.key === u.assigned_solution_area)?.label || u.assigned_solution_area.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()))
                    : null;
                  return (
                    <TableRow key={u.user_id} className="hover:bg-[#F4F3F5]/50" data-testid={`app-user-row-${u.user_id}`}>
                      <TableCell>
                        <div>
                          <div className="font-medium text-sm text-[#091F2C]">
                            {u.first_name} {u.last_name}
                          </div>
                          <div className="text-xs text-[#94A3B8]">{u.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {u.domain_type && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-600">
                            <Globe className="w-3 h-3" />
                            {u.domain_type}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className="text-xs font-medium flex items-center gap-1 w-fit border-0"
                          style={{ backgroundColor: `${rc.color}12`, color: rc.color }}
                        >
                          <RoleIcon className="w-3 h-3" />
                          {rc.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {u.role === "resource_content_manager" && (
                          <span className="text-xs text-[#07641D] font-medium">
                            {saDisplay || "All Solution Areas"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs cursor-pointer ${u.status === "active" ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-red-100 text-red-700 hover:bg-red-200"}`}
                          onClick={() => handleStatusToggle(u.user_id, u.status)}
                          data-testid={`toggle-status-${u.user_id}`}
                        >
                          {updatingUser === u.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : (u.status || "active")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-[#94A3B8]">
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Never"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs gap-1.5"
                          onClick={() => openRoleEditor(u)}
                          disabled={updatingUser === u.user_id}
                          data-testid={`edit-role-btn-${u.user_id}`}
                        >
                          <UserCog className="w-3.5 h-3.5" /> Edit Role
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-5 py-3 border-t border-black/5 flex items-center justify-between">
                <span className="text-xs text-[#64748B]">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="app-users-prev">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="app-users-next">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Role Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(o) => !o && setEditDialog({ open: false, user: null })}>
        <DialogContent className="max-w-md" data-testid="role-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-4 h-4 text-[#194449]" />
              Edit Role — {editDialog.user?.email}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-[#091F2C] mb-2 block">Role</label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger className="w-full" data-testid="role-edit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">MyGoals User</SelectItem>
                  <SelectItem value="admin">MyGoals Admin</SelectItem>
                  <SelectItem value="microsoft_manager">Microsoft Manager</SelectItem>
                  <SelectItem value="resource_content_manager">Content Manager (RCM)</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Solution Area — only shown for resource_content_manager */}
            {editRole === "resource_content_manager" && (
              <div>
                <label className="text-sm font-medium text-[#091F2C] mb-2 block">Solution Area Scope</label>
                <Select value={editSolutionArea} onValueChange={setEditSolutionArea}>
                  <SelectTrigger className="w-full" data-testid="solution-area-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Solution Areas</SelectItem>
                    {solutionAreas.map(sa => (
                      <SelectItem key={sa.key} value={sa.key}>{sa.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#94A3B8] mt-1.5">
                  Content Managers can only manage resources within their assigned solution area.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog({ open: false, user: null })} data-testid="role-edit-cancel">Cancel</Button>
            <Button
              onClick={saveRoleChange}
              disabled={saving}
              className="bg-[#194449] hover:bg-[#0f2e31] text-white"
              data-testid="role-edit-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
